<template>
  <div class="modal-overflow">
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label>Name</label>
          <el-input type="text"
                 name="name"
                 placeholder="Name"
                 clearable
                 v-model="edit.name"
                 v-validate="modelValidations.name" />
          <small class="text-danger" v-show="errors.has('name')">
            {{ getError('name') }}
          </small>
        </div>
      </div>
    </div>
    <div class="form-group">
      <label>Quickbooks Class</label>
      <el-select class="select"
                size="large"
                name="qbClass"
                data-vv-as="Quickbooks Class"
                placeholder="Quickbooks Class"
                v-model="edit.qbClass"
                v-validate="modelValidations.qbClass">
        <el-option v-for="qbClass in constants.QUICKBOOKS.CLASSES"
                  class="select"
                  :value="qbClass"
                  :label="qbClass"
                  :key="qbClass">
        </el-option>
      </el-select>
      <small class="text-danger" v-show="errors.has('qbClass')">
        {{ getError('qbClass') }}
      </small>
    </div>
    <div class="form-group">
      <label>Department</label>
      <el-select class="select"
                name="department"
                size="large"
                clearable
                data-vv-as="department"
                placeholder="Department"
                v-validate="modelValidations.department"
                v-model="edit.departmentId">
        <el-option v-for="departmentId in departments"
                  class="select"
                  :value="departmentId.value"
                  :label="departmentId.label"
                  :key="departmentId.value"/>
      </el-select>
      <small class="text-danger" v-show="errors.has('department')">
        {{ getError('department') }}
      </small>
    </div>
    <div class="form-group">
      <label>Description</label>
      <el-select class="select"
                  size="large"
                  clearable
                  style="margin-bottom: 6px;"
                  @change="appendToDescription"
                  v-model="descriptionBuilder"
                  placeholder="Select to add to Item Description">
        <el-option v-for="option in descriptionOptions"
                    class="select"
                    :value="option.value"
                    :label="option.label"
                    :key="option.label">
        </el-option>
      </el-select>
      <el-input type="textarea"
                autosize
                placeholder="Leave blank for QB default"
                v-model="edit.description" />
    </div>
    <div class="row" style="margin-top: 15px">
      <div class="col-md-12">
        <button type="submit" :disabled="isSubmitDisabled" @click.prevent="validateAndSubmit" class="btn btn-fill btn-info btn-wd pull-right">Save Item Code</button>
        <div class="form-group form-group-active-label">
          <label class="active-label">Active</label>
          <p-switch v-model="edit.active" type="primary" on-text="YES" off-text="NO"></p-switch>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {sync} from 'vuex-pathify'
  import {mapGetters} from 'vuex'
  import {notifySuccess, notifyError} from 'src/utilities/utils'
  import {Table, TableColumn, Select, Option, Autocomplete} from 'element-ui'
  import Constants from 'src/utilities/constants'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import {OK} from 'http-status-codes'

  export default{
    props: ['data'],
    components: {
      Table,
      TableColumn,
      [Select.name]: Select,
      [Option.name]: Option,
      [Autocomplete.name]: Autocomplete,
      PSwitch
    },
    computed: {
      ...mapGetters({
        departments: 'departments/kvpsForSelect'
      }),
      isSubmitDisabled() {
        return this.isProcessing
      },
      edit: sync('quickbooks@edit'),
    },
    data() {
      return {
        descriptionBuilder: null,
        constants: Constants,
        isProcessing: false,
        modelValidations: {
          name: {
            required: true,
            max: 64
          },
          qbClass: {
            required: true
          },
          department: {
            required: true
          }
        },
        descriptionOptions: [
          {
            value: '{{Approval Status}}',
            label: 'Approval Status'
          },
          {
            value: '{{Block Number}}',
            label: 'Block Number'
          },
          {
            value: '{{Builder}}',
            label: 'Builder'
          },
          {
            value: '{{Community}}',
            label: 'Community'
          },
          {
            value: '{{County}}',
            label: 'County'
          },
          {
            value: '{{Inspection Result}}',
            label: 'Inspection Result'
          },
          {
            value: '{{Job Name}}',
            label: 'Job Name'
          },
          {
            value: '{{Job Name or Task Name}}',
            label: 'Job Name or Task Name'
          },
          {
            value: '{{Legal Description}}',
            label: 'Legal Description'
          },
          {
            value: '{{Lot Number}}',
            label: 'Lot Number'
          },
          {
            value: '{{Permit Number}}',
            label: 'Permit Number'
          },
          {
            value: '{{Phase}}',
            label: 'Phase'
          },
          {
            value: '{{Plan Name}}',
            label: 'Plan Name'
          },
          {
            value: '{{Plan Number}}',
            label: 'Plan Number'
          },
          {
            value: '{{P.O. Number}}',
            label: 'P.O. Number'
          },
          {
            value: '{{Project Address}}',
            label: 'Project Address'
          },
          {
            value: '{{Project Name}}',
            label: 'Project Name'
          },
          {
            value: '{{Project Type}}',
            label: 'Project Type'
          },
          {
            value: '{{PWS Number}}',
            label: 'PWS Number'
          },
          {
            value: '{{Reinspection Count}}',
            label: 'Reinspection Count'
          },
          {
            value: '{{Repeat Count}}',
            label: 'Repeat Count'
          },
          {
            value: '{{Section}}',
            label: 'Section'
          },
          {
            value: '{{Task Completion Date}}',
            label: 'Task Completion Date'
          },
          {
            value: '{{Task Name}}',
            label: 'Task Name'
          }
        ]
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      async validateAndSubmit() {
        this.isProcessing = true

        if (await this.$validator.validateAll()) {
          const payload = this.$store.get('quickbooks/savePayload', 'item-codes')
          const response = await this.$store.dispatch('save', payload)

          if (response.result === Constants.RETURN_CODES.SUCCESS) {
            notifySuccess(this, 'Item Code Saved Successfully')
            this.clearCache()
            this.$eventHub.$emit('itemCodeSaved')
            this.$emit('close')
          } else {
            notifyError(this, 'Failed to Save Item Code', response.errors)
          }
        }
        this.isProcessing = false
      },
      clearCache() {
        // purge the items codes cache after an item code has been added or edited
        if (this.$store.cache.has('quickbooks/fetchAll')) {
          this.$store.cache.delete('quickbooks/fetchAll')
        }
      },
      appendToDescription(selectedOption) {
        if (selectedOption) {
          this.$store.commit('quickbooks/appendToDescription', selectedOption)
          this.descriptionBuilder = null
        }
      }
    },
    async mounted() {
      this.$store.commit('quickbooks/resetEdit')
      if (this.data && this.data.id) {
        const response = await this.$store.dispatch('fetchById', {endpoint: 'item-codes', id: this.data.id})

        if (OK !== response.status) {
          notifyError(this, 'Failed to load item code with id: ' + this.data.id)
          return
        }

        this.$store.commit('quickbooks/hydrateEdit', response.data)
      }
    }
  }
</script>

<style scoped>
  label {
    display: block;
  }
</style>
